import { ofetch } from 'ofetch'
import { showErrorToast } from "@/utils/toast"
import { getI18n } from "@/plugins/0.i18n"
import { useUserStore } from "@/stores/UserStore"
import debounce from 'lodash/debounce'
import { useOnline } from '@vueuse/core'
import { useCookie } from "@core/composable/useCookie"

// const email = import.meta.env.VITE_BACKEND_TEST_USER_EMAIL
// const password = import.meta.env.VITE_BACKEND_TEST_USER_PASSWORD
const basicAuth = import.meta.env.VITE_BACKEND_TEST_BASIC_AUTH // Basic Auth base64 encoded string
const i18n = getI18n().global
const isOnline = useOnline()

// Needed to abort old requests
const abortControllers = new Map()

// Debounced version of the error toast function
const debouncedShowErrorToast = debounce((message) => {
  showErrorToast(message)
}, 100)


// Function to abort a backend request by its key
export const abortBackendRequest = (requestKey) => {
  const controller = abortControllers.get(requestKey)
  try {
    if (controller) {
      controller.abort()
      abortControllers.delete(requestKey)
    }
  } catch (error) {
    console.warn('Error aborting request:', error)
  }

}


// Function to check if a backend request is pending
export const isBackendRequestPendingThatStartWith = (requestKey) => {
  // Extract the base endpoint (everything before the "?")
  for (const key of abortControllers.keys())
    if (key.startsWith(requestKey)) return true

  return false
}

// Function to check if a backend request is pending
export const isBackendRequestPending = (requestKey) => {
  // Extract the base endpoint (everything before the "?")
  const baseEndpoint = requestKey.split('?')[0]

  // Check if any existing request key contains this endpoint path
  for (const key of abortControllers.keys()) {
    const keyPath = key.split('?')[0]
    if (keyPath.includes(baseEndpoint) || baseEndpoint.includes(keyPath)) {
      console.log('Found pending request:', key, 'for', requestKey)

      return true
    }
  }

  // For debugging - log all keys when no match is found
  // console.log('No pending request found for:', requestKey)
  //console.log('Current pending requests:', Array.from(abortControllers.keys()))

  return false
}

// TODO: Check this function performance
// Function to abort all requests that start with a specific key
export const abortRequestsGetThatStartsWith = (requestKey) => {
  for (const key of abortControllers.keys()) {

    if (key.startsWith(requestKey))  {
      //
      //console.log('Found pending request:', key, 'for', requestKey, typeof key)
      //
      const controller = abortControllers.get(key)

      if (controller) {
        //
        //console.log('Aborting request:', key)
        //
        controller.abort()
        abortControllers.delete(key)
      }
    }
  }
  // For debugging - log all keys when no match is found
  //console.log('Current pending requests:', Array.from(abortControllers.keys()))

  return true
}


export const $backend = ofetch.create({
  baseURL: import.meta.env.VITE_BACKEND_SERVER_URL,
  credentials: 'include', // Needed to include ALL cookies with request
  async onRequest({ request, options }) {

    const userStore = useUserStore()
    let accessToken = useCookie('accessToken').value

    if (userStore) {
      if (!accessToken) {
        if(import.meta.env?.VITE_LOGIN_TO_TEST_SALON === "false") {
          if(typeof userStore.fetchAccessTokenByCookie === 'function') {
            accessToken = await userStore.fetchAccessTokenByCookie() // B: normlane logowanie przy pomocy cookisa dla wszystkich
          }
        } else { // true
          if(typeof userStore.fetchAccessTokenByEmail === 'function') {
            accessToken = await userStore.fetchAccessTokenByEmail() // B: chcemy dostac sie do salonu Alicji (uzywane tylko na localhost)
          }
        }

      }

      if (accessToken) {
        useCookie('accessToken').value = accessToken
        // Check if we're dealing with FormData
        const isFormData = options.body instanceof FormData

        // Set default headers but don't set Content-Type for FormData
        options.headers = {
          'Basic-Authorization': `Basic ${basicAuth}`,
          // Only set default Content-Type if not FormData
          ...(!isFormData && { 'Content-Type': 'application/json' }),
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        }
      }

    }



    // Chcek for xdebug=1 in browser url - added for Lukasz
    if (import.meta.env?.VITE_DEVELOPMENT_MODE && typeof window !== 'undefined' && window.location.search.includes('xdebug=1')) {
      options.query = options.query || {}
      options.query.XDEBUG_SESSION_START = 'PHPSTORM'
    }

    if (options.disableAbortController) return // Skip abort controller if needed

    // Create a key based on the URL
    const requestKey = options.url || request
    // If there is an active request to the same endpoint, cancel it
    if (abortControllers.has(requestKey)) {
      abortControllers.get(requestKey).abort()
    }
    const controller = new AbortController() // Create a new `AbortController`
    options.signal = controller.signal
    abortControllers.set(requestKey, controller) // Store the controller in the map

    // Keep the exact key used above. ofetch appends baseURL + options.query to
    // context.request AFTER onRequest, so rebuilding the key from the final URL
    // does not match (e.g. the XDEBUG_SESSION_START param) and the entry leaks.
    options._requestKey = requestKey
    //console.log('options._requestKey', options._requestKey)
  },
  async onResponse({ options }) {
    // Remove the `AbortController` as the request has completed
    abortControllers.delete(options._requestKey)
  },
  async onRequestError({ request, options, error }) {
    abortControllers.delete(options._requestKey)
    console.warn('onRequestError', request, error)
    if (error.name === 'AbortError') {
      console.warn('Request aborted:', request)

      return // Do not show an error if it was aborted
    }

    if(request.indexOf('userActivitySession')>0) return // Dont show errors from specific endpoints

    if(isOnline.value) debouncedShowErrorToast(i18n.t('fetch_errors.request_error')) // If online -> problem with server
    else debouncedShowErrorToast(i18n.t('cant_perform_this_action_there_is_no_internet')) // If offline -> show problem with internet connection
    console.warn('Request error:', error)
  },
  async onResponseError({ request, options, response }) {
    abortControllers.delete(options._requestKey)
    console.warn('onResponseError', request, response)
    //TODO B: jak wyjdziemy z uposledzonego serwera testowego to trzeba sprawdzic
    // jakie sa statusy odpowiedzi bo teraz nawet przy 401 w pierwszej kolejnosci wchodzi CORS error

    if (response.status === 401) {
      const accessToken = useCookie('accessToken')
      accessToken.value = null

      // useCookie('panelVersion', {
      //   domain: import.meta.env.VITE_COOKIE_DOMAIN,
      //   secure: true,
      //   sameSite: 'Lax',
      // }).value = 1
      window.location.href = `${import.meta.env.VITE_SALONER10_URL}login/logout/`

      //const retryCount = useCookie('retryCount')
      //console.warn('Unauthorized', retryCount.value)
      // if(Number(retryCount.value) >= 1) {
      //   retryCount.value = 0
      //   useCookie('panelVersion', {
      //     domain: import.meta.env.VITE_COOKIE_DOMAIN,
      //     secure: true,
      //     sameSite: 'Lax',
      //   }).value = 1
      //   window.location.href = `${import.meta.env.VITE_SALONER10_URL}login`
      // } else {
      //   retryCount.value = 1
      //   window.location.reload()
      // }

      return
    }

    showErrorToast(response._data.message || i18n.t('fetch_errors.response_error'))
    //console.warn('Response error:', response.status, await response.text())

  },
  async onError({ request, error }) {
    console.warn('onError', request, error)
    if (error.name === 'AbortError') {
      console.warn('Request aborted:', request)

      return // Do not display an error for an aborted request
    }
    showErrorToast(i18n.t('fetch_errors.unhandled_error'))
    console.warn('Unhandled error:', error)
  },
})
